import React, {FC} from 'react';

import {useLocalize} from '../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {extractInitials} from '../../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {Avatar} from '../../../../libs/shared/ui/src/components/avatar/avatar';
import {AvatarBackground} from '../../../../libs/shared/ui/src/components/avatar/avatarBackground';
import {AvatarEditor} from '../../../../libs/shared/ui/src/components/avatar/avatarEditor';
import {AvatarInitials} from '../../../../libs/shared/ui/src/components/avatar/avatarInitials';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {buildNoNameNoHandlesCardDisplayName} from '../../../helpers/models/cardHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {ContactAvatar} from '../../common/avatars/contactAvatar';
import {useContactDetailsContext} from '../contactDetailsContext';

/*
 * Props.
 */

interface ContactHeaderAvatarProps {
  contact?: VisualContactModel;
  card: CardModel | undefined;
  updateCardAvatarAsync: (card: CardModel, file: File) => Promise<void>;
}

/*
 * Component.
 */

export const ContactHeaderAvatar: FC<ContactHeaderAvatarProps> = (props) => {
  const {card, contact, updateCardAvatarAsync} = props;
  const contactDetailsContext = useContactDetailsContext();
  const localize = useLocalize();

  const onFileChange = async (file: File) => {
    if (!card) {
      return;
    }

    await updateCardAvatarAsync(card, file);
  };

  return (
    <AvatarEditor
      avatar={renderAvatar(localize, contact, card)}
      shouldNotShowControls={contactDetailsContext.isReadOnly || !card || !contact}
      onFileChange={onFileChange}
    />
  );
};

/*
 * Helpers.
 */

function renderAvatar(localize: Localizer, contact?: VisualContactModel, card?: CardModel) {
  if (!contact) {
    return renderNoContactAvatar(card?.name ?? buildNoNameNoHandlesCardDisplayName(localize));
  }
  return <ContactAvatar visualContact={contact} size={VisualSizesEnum.EXTRA_LARGE} />;
}

function renderNoContactAvatar(name: string) {
  const initials = extractInitials(name);
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Avatar size={VisualSizesEnum.EXTRA_LARGE}>
          <AvatarBackground color={theme.greys.shade70} />
          <AvatarInitials
            size={VisualSizesEnum.EXTRA_LARGE}
            textColor={theme.invertedText}
            value={initials}
          />
        </Avatar>
      )}
    </UserThemeConsumer>
  );
}
